import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'

import { httpClient } from '@/services/http/httpClient'
import type { PaginationResponse } from '@/types/paginationResponse'
import { getQueryParams, type QueryParams } from '@/utils/queryParams'

type NotificationType =
  | 'CHALLENGE_MANAGER'
  | 'CHALLENGE_SPECIALIST'
  | 'IDEA_SUBMITTED'
  | 'COMMENT_REPLY'
  | 'IDEA_REJECTED'
  | 'IDEA_REACTIVATED'
  | 'INVITATION'
  | 'REMOVAL'
  | 'INVITATION_DECLINED'
  | 'REQUEST_CHANGES'
  | 'TECHNICAL_REQUEST'
  | 'TECHNICAL_RESPONSE'
  | 'EVALUATORS_ASSIGNED'
  | 'CHALLENGE_MANAGER'
  | 'CHALLENGE_SPECIALIST'
  | 'PORTFOLIO_INVITE'
  | 'PROJECT_INVITE'
  | 'SYSTEM'

type NotificationHttpResponse = {
  id: string
  type: NotificationType
  title: string
  description?: string
  isRead: boolean
  createdAt: string
  link?: string
  relatedIdeaId?: string
  comment?: string
  relatedPortfolioId?: string
  relatedProjectId?: string
  originUser?: {
    id: string
    name: string
    email: string
    image?: string
  }
}

type Notification = NotificationHttpResponse & {
  relativeCreatedAt: string
}

type NotificationsHttpResponse = {
  notifications: NotificationHttpResponse[]
  paginate: PaginationResponse
}

type NotificationsResponse = {
  notifications: Notification[]
  pagination: PaginationResponse
}

type Params = {
  query: QueryParams
}

async function getUserNotifications({
  query,
}: Params): Promise<NotificationsResponse> {
  const queryParams = getQueryParams(query)

  const { data } = await httpClient.get<NotificationsHttpResponse>(
    `/events/v2?${queryParams}`,
  )

  const notifications = data.notifications.map((notification) => ({
    id: notification.id,
    type: notification.type,
    title: notification.title,
    description: notification.description,
    isRead: notification.isRead,
    relativeCreatedAt: notification.createdAt
      ? formatDistanceToNow(new Date(notification.createdAt), {
          addSuffix: true,
          includeSeconds: true,
          locale: ptBR,
        })
      : '',
    createdAt: notification.createdAt,
    relatedIdeaId: notification.relatedIdeaId,
    link: notification.link,
    comment: notification.comment,
    originUser: notification.originUser,
    relatedPortfolioId: notification.relatedPortfolioId,
    relatedProjectId: notification.relatedProjectId,
  }))

  return {
    notifications,
    pagination: data.paginate,
  }
}

export { getUserNotifications, type Notification }
